interface Feature {
	name: string;
	free: boolean | string;
	premium: boolean | string;
	tooltip?: string;
	manualLink?: string;
}

const features: Feature[] = [
	// Features available in both Free and Premium (checked in Free)
	{
		name: "Create and manage events",
		free: true,
		premium: true,
		manualLink: "/manual#events",
	},
	{
		name: "Event RSVPs and notifications",
		free: true,
		premium: true,
		manualLink: "/manual#events",
	},
	{
		name: "Event editing and deletion",
		free: true,
		premium: true,
		manualLink: "/manual#events",
	},
	{
		name: "Event reminders",
		free: true,
		premium: true,
		manualLink: "/manual#events",
	},
	{
		name: "Event threads",
		free: true,
		premium: true,
		manualLink: "/manual#event_threads",
	},
	{
		name: "Event and poll channels",
		free: true,
		premium: true,
		manualLink: "/manual#event_and_poll_channels",
	},
	{
		name: "Create and manage polls",
		free: true,
		premium: true,
		manualLink: "/manual#polls",
	},
	{
		name: "Anonymous voting",
		free: true,
		premium: true,
		manualLink: "/manual#polls",
	},
	{
		name: "Personal reminders",
		free: true,
		premium: true,
		manualLink: "/manual#remind",
	},
	{
		name: "User preferences",
		free: true,
		premium: true,
		manualLink: "/manual#user_preferences",
	},
	{
		name: "Server notifications",
		free: true,
		premium: true,
		manualLink: "/manual#server_notifications",
	},

	// Features with differences between Free and Premium
	{
		name: "Event templates",
		free: "3 favorites per server",
		premium: "Unlimited favorites",
		manualLink: "/manual#event_templates",
	},
	{
		name: "Server logs",
		free: "24 hours",
		premium: "Unlimited history",
		manualLink: "/manual#server_logs",
	},
	{
		name: "Outbound calendar sync",
		free: "1 per user/server",
		premium: "5 per user/server",
		manualLink: "/manual#google_calendar_sync",
	},
	{
		name: "Google Calendar integration",
		free: "Basic",
		premium: "Full two-way sync",
		manualLink: "/manual#google_calendar_sync",
	},
	{
		name: "AI command usage",
		free: "Limited",
		premium: "Higher limits",
		manualLink: "/manual#ai",
	},

	// Premium-only features
	{
		name: "Recurring/repeating events",
		free: false,
		premium: true,
		manualLink: "/manual#repeating_events",
	},
	{
		name: "Custom RSVP options with limits",
		free: false,
		premium: true,
		manualLink: "/manual/#custom_rsvp_options",
	},
	{
		name: "Attendee role automation",
		free: false,
		premium: true,
		manualLink: "/manual#attendee_roles",
	},
	{
		name: "Role-based RSVP restrictions",
		free: false,
		premium: true,
		manualLink: "/manual#role_restrictions",
	},
	{
		name: "Close RSVPs early",
		free: false,
		premium: true,
		manualLink: "/manual#close_rsvps_early",
	},
	{
		name: "Role-based poll restrictions",
		free: false,
		premium: true,
		manualLink: "/manual#role_restrictions",
	},
	{
		name: "Unlimited live list",
		free: false,
		premium: true,
		manualLink: "/manual#live_list",
	},
	{
		name: "Inbound calendar sync",
		free: false,
		premium: "5 per user/server",
		manualLink: "/manual#google_calendar_sync",
	},
	{
		name: "Bidirectional calendar sync",
		free: false,
		premium: "5 per user/server",
		manualLink: "/manual#google_calendar_sync",
	},
	{ name: "Time Finder advanced settings", free: false, premium: true },
];

function FeatureValue({ value }: { value: boolean | string }) {
	if (typeof value === "boolean") {
		return value ? (
			<div className="flex justify-center">
				<i className="fas fa-check text-green-400"></i>
			</div>
		) : (
			<div className="flex justify-center">
				<i className="fas fa-times text-slate-500"></i>
			</div>
		);
	}

	return <div className="text-center text-sm text-slate-300">{value}</div>;
}

export function FeatureComparisonTable() {
	return (
		<div className="mx-auto max-w-4xl px-4 py-16">
			<h2 className="mb-12 text-center text-4xl font-bold tracking-tight text-white">
				Free vs Premium features
			</h2>

			<div className="overflow-hidden rounded-xl border border-slate-700 bg-slate-800/30">
				{/* Header */}
				<div className="grid grid-cols-3 border-b border-slate-700 bg-slate-800/50">
					<div className="p-4 text-left">
						<span className="text-lg font-semibold text-white">
							Features
						</span>
					</div>
					<div className="border-l border-slate-700 p-4 text-center">
						<span className="text-lg font-semibold text-white">
							Free
						</span>
					</div>
					<div className="border-l border-slate-700 p-4 text-center">
						<span className="text-lg font-semibold text-sesh-primary">
							Premium
						</span>
					</div>
				</div>

				{/* Feature rows */}
				{features.map((feature, index) => (
					<div
						key={index}
						className={`grid grid-cols-3 border-b border-slate-700/50 last:border-b-0 ${
							index % 2 === 0
								? "bg-slate-800/20"
								: "bg-slate-800/40"
						}`}
					>
						<div className="p-4 text-left">
							{feature.manualLink ? (
								<a
									href={feature.manualLink}
									target="_blank"
									rel="noopener noreferrer"
									className="flex items-center gap-2 text-slate-200 transition-colors duration-150 hover:text-slate-100"
									title="Learn more"
								>
									<span>{feature.name}</span>
									<i className="fas fa-info-circle text-xs text-slate-400"></i>
								</a>
							) : (
								<span className="text-slate-200">
									{feature.name}
								</span>
							)}
						</div>
						<div className="border-l border-slate-700/50 p-4">
							<FeatureValue value={feature.free} />
						</div>
						<div className="border-l border-slate-700/50 p-4">
							<FeatureValue value={feature.premium} />
						</div>
					</div>
				))}
			</div>

			<div className="mt-8 text-center">
				<p className="text-sm text-slate-400">
					All premium features include everything from the free tier,
					plus additional capabilities.
				</p>
			</div>
		</div>
	);
}
